import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, doc, getDocs, setDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { Squad, User } from '../../../types';
import { useFormConfiguration, FormQuestion } from '../hooks/useFormConfiguration';
import SessionStatsAccordion from './SessionStatsAccordion';
import { EmailService } from '../../../services/EmailService';
import { ClassroomTeam, PortfolioEntry } from '../classroom/types';
import { ResolvedSessionPlan } from '../utils/sessionPlan';
import UploadPhotosModal from './classroom/UploadPhotosModal';
import '../styles/FeedbackModal.css';

const DEVICES_BROUGHT_KEY = 'devicesBrought';
const DEVICES_START_KEY = 'devicesAtStart';
const DEVICES_END_KEY = 'devicesAtEnd';
const DEVICES_NEXT_KEY = 'devicesNextRecipient';

interface FeedbackModalProps {
  squad: Squad;
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  onCancelSession: (payload: { reason: 'accidental' | 'otherTeacher' | 'preview' | 'other'; otherText?: string }) => Promise<void> | void;
  teacherId: string;
  teacherName: string;
  achievementProgress: Record<string, Record<string, number>>;
  /** Classroom Board: portfolio upload status + worksheet photo button */
  teams?: ClassroomTeam[];
  entries?: PortfolioEntry[];
  plan?: ResolvedSessionPlan;
  pagerIndex?: number;
}

type FeedbackDraft = {
  v: 1;
  updatedAt: number;
  formData: Record<string, any>;
  sessionId?: string;
  squadId?: string;
  teacherId?: string;
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  squad,
  isOpen,
  onClose,
  onSubmitted,
  onCancelSession,
  teacherId,
  teacherName,
  achievementProgress,
  teams,
  entries,
  plan,
  pagerIndex,
}) => {
  const { formConfig, loading: formLoading, error: formError } = useFormConfiguration('v1');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [students, setStudents] = useState<User[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [showCancelLevelModal, setShowCancelLevelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState<'accidental' | 'otherTeacher' | 'preview' | 'other' | ''>('');
  const [cancelReasonOther, setCancelReasonOther] = useState('');
  const [isCanceling, setIsCanceling] = useState(false);
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState<number | null>(null);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const draftKey = useMemo(() => {
    const sessionId = squad.liveSession?.sessionId;
    if (!sessionId || !teacherId) return null;
    // Keyed per teacher + session so drafts never collide.
    return `feedbackDraft:v1:${teacherId}:${sessionId}`;
  }, [squad.liveSession?.sessionId, teacherId]);

  const clearDraft = useCallback(() => {
    if (!draftKey) return;
    try {
      localStorage.removeItem(draftKey);
    } catch {
      // Ignore storage errors (private mode, disabled storage, etc.)
    }
  }, [draftKey]);

  const saveDraftNow = useCallback(
    (data: Record<string, any>) => {
      if (!draftKey) return;
      // Avoid creating empty drafts.
      if (!data || Object.keys(data).length === 0) return;

      const payload: FeedbackDraft = {
        v: 1,
        updatedAt: Date.now(),
        formData: data,
        sessionId: squad.liveSession?.sessionId,
        squadId: squad.id,
        teacherId,
      };

      try {
        localStorage.setItem(draftKey, JSON.stringify(payload));
        setLastDraftSavedAt(payload.updatedAt);
      } catch {
        // Ignore storage errors (quota, private mode, disabled storage, etc.)
      }
    },
    [draftKey, squad.id, squad.liveSession?.sessionId, teacherId]
  );

  const handleRequestClose = useCallback(() => {
    // Force-save immediately in case they close right after typing.
    saveDraftNow(formData);
    onClose();
  }, [formData, onClose, saveDraftNow]);

  // Map specific questions by label so we can add conditional logic & custom UIs
  const devicesQuestionIds = useMemo(() => {
    if (!formConfig) return {};
    const norm = (s?: string) => (s || '').toLowerCase().trim();
    const findByMatcher = (matcher: (label: string) => boolean) =>
      formConfig.questions.find(q => matcher(norm(q.label)))?.id;

    return {
      // e.g. "Devices brought to the class?"
      broughtId: findByMatcher(label =>
        label.includes('devices brought') ||
        label.includes('bring devices') ||
        label.includes('devices to class')
      ),
      // e.g. "Devices at end of class?"
      countStartId: findByMatcher(label =>
        label.includes('devices at start') ||
        label.includes('devices at the start') ||
        label.includes('devices brought to the class?') // legacy
      ),
      countEndId: findByMatcher(label =>
        label.includes('devices at end') ||
        label.includes('devices do you have now')
      ),
      nextRecipientId: findByMatcher(label =>
        label.includes('who are you giving the devices to next') ||
        label.includes('who are you giving the devices to')
      ),
    } as {
      broughtId?: string;
      countStartId?: string;
      countEndId?: string;
      nextRecipientId?: string;
    };
  }, [formConfig]);

  // Map incident / bug question IDs by label so we can surface them cleanly in Slack
  const incidentQuestionIds = useMemo(() => {
    if (!formConfig) return {};
    const norm = (s?: string) => (s || '').toLowerCase().trim();
    const findByMatcher = (matcher: (label: string) => boolean) =>
      formConfig.questions.find(q => matcher(norm(q.label)))?.id;

    return {
      safetyId: findByMatcher(label => label.includes('safety incident')),
      otherId: findByMatcher(label => label.includes('other notable incidents')),
      glitchesId: findByMatcher(label => label.includes('glitches') || label.includes('bugs')),
    } as {
      safetyId?: string;
      otherId?: string;
      glitchesId?: string;
    };
  }, [formConfig]);

  const orderedQuestions = useMemo(() => {
    if (!formConfig) return [];
    const qs = [...formConfig.questions];
    const norm = (s?: string) => (s || '').toLowerCase();

    const mainReflectionIndex = qs.findIndex(q => {
      const l = norm(q.label);
      return l.includes('main reflection');
    });

    const learningsIndex = qs.findIndex(q => {
      const l = norm(q.label);
      return l.includes('learnings to share') || l.includes('make it a 10/10');
    });

    const ratingIndex = qs.findIndex(q => {
      const l = norm(q.label);
      return l.includes('rate this session') || l.includes('session out of 10');
    });

    if (mainReflectionIndex === -1 || ratingIndex === -1 || learningsIndex === -1) {
      return qs;
    }

    const mainReflection = qs[mainReflectionIndex];
    const rating = qs[ratingIndex];
    const learnings = qs[learningsIndex];
    const others = qs.filter(
      (_, idx) => idx !== mainReflectionIndex && idx !== ratingIndex && idx !== learningsIndex
    );

    // Desired order: Main Reflection → Rating → Learnings → all others
    return [mainReflection, rating, learnings, ...others];
  }, [formConfig]);

  // Fallback device question mapping based on label order if explicit patterns don't match
  const effectiveDevicesQuestionIds = useMemo(() => {
    if (!formConfig) return devicesQuestionIds;

    let { broughtId, countStartId, countEndId, nextRecipientId } = devicesQuestionIds;

    if (!broughtId || !countStartId || !countEndId) {
      const deviceQuestions = formConfig.questions.filter(q =>
        (q.label || '').toLowerCase().includes('device')
      );

      if (deviceQuestions.length) {
        // Assume first is "did you bring devices", second is start count, third is end count
        broughtId = broughtId || deviceQuestions[0]?.id;
        countStartId = countStartId || deviceQuestions[1]?.id;
        countEndId = countEndId || deviceQuestions[2]?.id;

        if (!nextRecipientId) {
          const whoQ = deviceQuestions.find(q =>
            (q.label || '').toLowerCase().includes('who are you giving')
          );
          nextRecipientId = whoQ?.id || nextRecipientId;
        }
      }
    }

    return { broughtId, countStartId, countEndId, nextRecipientId };
  }, [formConfig, devicesQuestionIds]);

  const isFormValid = useMemo(() => {
    if (!formConfig) {
      return false;
    }

    return formConfig.questions.every(question => {
      // Devices follow-up questions are only required if teacher brought devices
      if (
        effectiveDevicesQuestionIds.broughtId &&
        (question.id === effectiveDevicesQuestionIds.countStartId ||
          question.id === effectiveDevicesQuestionIds.countEndId ||
          question.id === effectiveDevicesQuestionIds.nextRecipientId)
      ) {
        const bringValue = formData[effectiveDevicesQuestionIds.broughtId];
        const saidYes = bringValue === true || bringValue === 'yes';
        if (!saidYes) {
          // Skip validation for these fields when devices were not brought
          return true;
        }
      }

      if (!question.required) {
        return true;
      }
      
      const value = formData[question.id];
      
      // Handle different types of "empty"
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;

      // Handle groups that might have required sub-questions
      if (question.type === 'group' && Array.isArray(question.questions)) {
        return question.questions.every(subQuestion => {
          if (!subQuestion.required) return true;
          const subValue = formData[question.id]?.[subQuestion.id];
          if (subValue === undefined || subValue === null) return false;
          if (typeof subValue === 'string' && subValue.trim() === '') return false;
          return true;
        });
      }

      // Handle boolean-with-details where details become required
      if (question.type === 'boolean-with-details' && value === true) {
        const detailsValue = formData[question.detailsId];
        if (!detailsValue || (typeof detailsValue === 'string' && detailsValue.trim() === '')) {
          return false;
        }
      }

      return true;
    });
  }, [formData, formConfig]);

  useEffect(() => {
    // Restore autosaved draft (or start fresh) whenever the modal is opened.
    if (isOpen) {
      setRestoredFromDraft(false);
      setLastDraftSavedAt(null);

      if (!draftKey) {
        setFormData({});
      } else {
        try {
          const raw = localStorage.getItem(draftKey);
          if (!raw) {
            setFormData({});
          } else {
            const parsed = JSON.parse(raw) as Partial<FeedbackDraft>;
            if (parsed && typeof parsed === 'object' && parsed.formData && typeof parsed.formData === 'object') {
              setFormData(parsed.formData as Record<string, any>);
              setRestoredFromDraft(true);
              if (typeof parsed.updatedAt === 'number') {
                setLastDraftSavedAt(parsed.updatedAt);
              }
            } else {
              setFormData({});
            }
          }
        } catch {
          setFormData({});
        }
      }
    }

    const fetchStudents = async () => {
      if (!isOpen || !squad.studentIds || squad.studentIds.length === 0) {
        setStudentsLoading(false);
        setStudents([]);
        return;
      }
      
      setStudentsLoading(true);
      try {
        const studentsRef = collection(db, 'users');
        const q = query(studentsRef, where('__name__', 'in', squad.studentIds));
        const snapshot = await getDocs(q);
        const studentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
        setStudents(studentsData);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setStudentsLoading(false);
      }
    };

    fetchStudents();
  }, [isOpen, squad.studentIds, draftKey]);

  // Autosave feedback progress while the modal is open.
  useEffect(() => {
    if (!isOpen) return;
    if (!draftKey) return;

    const t = window.setTimeout(() => {
      saveDraftNow(formData);
    }, 500);

    return () => window.clearTimeout(t);
  }, [draftKey, formData, isOpen, saveDraftNow]);

  const handleInputChange = (id: string, value: any) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleOpenCancelLevel = () => {
    setCancelReason('');
    setCancelReasonOther('');
    setShowCancelLevelModal(true);
  };

  const handleConfirmCancelLevel = async () => {
    if (!cancelReason) {
      void window.appAlert('Please select a reason for canceling the level.');
      return;
    }
    if (cancelReason === 'other' && !cancelReasonOther.trim()) {
      void window.appAlert('Please provide a short explanation in the Other field.');
      return;
    }

    setIsCanceling(true);
    try {
      await onCancelSession({
        reason: cancelReason as 'accidental' | 'otherTeacher' | 'preview' | 'other',
        otherText: cancelReason === 'other' ? cancelReasonOther.trim() : undefined,
      });
      // If the session is being canceled, the feedback draft is no longer relevant.
      clearDraft();
      setShowCancelLevelModal(false);
    } catch (e) {
      console.error('Failed to cancel session from feedback modal', e);
      void window.appAlert('There was an error canceling this level. Please try again.');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleSubmit = async () => {
    if (!squad.liveSession?.sessionId) {
      console.error("Critical error: No session ID found. Cannot submit feedback.");
      return;
    }
    setIsSubmitting(true);

    // Check for "I don't know" condition
    if (formData.nextRecipient?.toLowerCase().trim() === "i don't know") {
      EmailService.triggerDeviceHandoffEmail({
        squadName: squad.title,
        teacherName: teacherName,
        teacherEmail: 'N/A', // The client doesn't have the teacher's email directly
        finishTime: new Date().toISOString(),
      });
    }

    const devicesStart =
      typeof formData[DEVICES_START_KEY] === 'number' ? formData[DEVICES_START_KEY] : undefined;
    const devicesEnd =
      typeof formData[DEVICES_END_KEY] === 'number' ? formData[DEVICES_END_KEY] : undefined;
    const deviceNextText =
      typeof formData[DEVICES_NEXT_KEY] === 'string' ? formData[DEVICES_NEXT_KEY].trim() : '';

    const safetyIncidentsText =
      incidentQuestionIds.safetyId && typeof formData[incidentQuestionIds.safetyId] === 'string'
        ? (formData[incidentQuestionIds.safetyId] as string).trim()
        : '';
    const otherIncidentsText =
      incidentQuestionIds.otherId && typeof formData[incidentQuestionIds.otherId] === 'string'
        ? (formData[incidentQuestionIds.otherId] as string).trim()
        : '';
    const glitchesText =
      incidentQuestionIds.glitchesId && typeof formData[incidentQuestionIds.glitchesId] === 'string'
        ? (formData[incidentQuestionIds.glitchesId] as string).trim()
        : '';

    const feedbackDoc: any = {
      squadId: squad.id,
      squadName: squad.title,
      sessionId: squad.liveSession.sessionId,
      teacherId: teacherId,
      teacherName: teacherName,
      lessonId: squad.currentLessonId,
      formVersion: formConfig?.version || 'v1',
      createdAt: serverTimestamp(),
      ...formData,
    };

    // Normalize device info into canonical fields for backend / Slack
    if (typeof devicesStart === 'number') {
      feedbackDoc.initialDeviceCount = devicesStart;
    }
    if (typeof devicesEnd === 'number') {
      feedbackDoc.finalDeviceCount = devicesEnd;
    }
    if (deviceNextText) {
      feedbackDoc.deviceHandoff = { nextRecipient: deviceNextText };
    }

    // Normalize incident text fields so backend can display them in Slack
    if (safetyIncidentsText) {
      feedbackDoc.safetyIncidents = safetyIncidentsText;
    }
    if (otherIncidentsText) {
      feedbackDoc.otherIncidents = otherIncidentsText;
    }
    if (glitchesText) {
      feedbackDoc.bugReportsText = glitchesText;
    }

    try {
      await setDoc(doc(db, 'classFeedback', squad.liveSession.sessionId), feedbackDoc);
      clearDraft();
      onSubmitted(); // This will call handleFinishClassWithFeedback
    } catch (err) {
      console.error("Error submitting feedback:", err);
      void window.appAlert("There was an error submitting your feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinishWithoutFeedback = async () => {
    if (!squad.liveSession?.sessionId) {
      console.error("Critical error: No session ID found. Cannot submit feedback.");
      return;
    }
    setIsSubmitting(true);

    const feedbackDoc = {
      squadId: squad.id,
      squadName: squad.title,
      sessionId: squad.liveSession.sessionId,
      teacherId: teacherId,
      teacherName: teacherName,
      lessonId: squad.currentLessonId,
      formVersion: formConfig?.version || 'v1',
      createdAt: serverTimestamp(),
      ...formConfig?.questions.reduce((acc, q) => {
        if (q.type === 'long-text' || q.type === 'text') {
          acc[q.id] = 'No Feedback';
        } else if (q.type === 'number') {
          acc[q.id] = 0;
        }
        return acc;
      }, {} as Record<string, any>),
    };

    try {
      await setDoc(doc(db, 'classFeedback', squad.liveSession.sessionId), feedbackDoc);
      clearDraft();
      onSubmitted();
    } catch (err) {
      console.error("Error submitting 'no feedback':", err);
      void window.appAlert("There was an error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question: FormQuestion) => {
    // Conditional visibility for device follow-up questions
    if (
      effectiveDevicesQuestionIds.broughtId &&
      (question.id === effectiveDevicesQuestionIds.countStartId ||
        question.id === effectiveDevicesQuestionIds.countEndId ||
        question.id === effectiveDevicesQuestionIds.nextRecipientId)
    ) {
      const bringValue = formData[effectiveDevicesQuestionIds.broughtId];
      const saidYes = bringValue === true || bringValue === 'yes';
      if (!saidYes) {
        return null;
      }
    }

    // Custom UI for "Did you bring devices to class?"
    if (effectiveDevicesQuestionIds.broughtId && question.id === effectiveDevicesQuestionIds.broughtId) {
      const value = formData[question.id] || '';
      return (
        <div className="yes-no-group">
          <label className="yes-no-option">
            <input
              type="radio"
              name={question.id}
              value="yes"
              checked={value === 'yes'}
              onChange={() => handleInputChange(question.id, 'yes')}
              disabled={isSubmitting || isCanceling}
            />
            <span>Yes</span>
          </label>
          <label className="yes-no-option">
            <input
              type="radio"
              name={question.id}
              value="no"
              checked={value === 'no'}
              onChange={() => handleInputChange(question.id, 'no')}
              disabled={isSubmitting || isCanceling}
            />
            <span>No</span>
          </label>
        </div>
      );
    }

    const lowerLabel = (question.label || '').toLowerCase();
    const isSafetyField = lowerLabel.includes('safety');
    const isNotableIncidentsField = lowerLabel.includes('notable incidents');
    const isGlitchesField = lowerLabel.includes('glitches') || lowerLabel.includes('bugs');
    const isSessionRatingField =
      lowerLabel.includes('rate this session') ||
      lowerLabel.includes('session out of 10');
    const forceLongText = isSafetyField || isNotableIncidentsField || isGlitchesField;

    const isDevicesCountStart =
      question.id === devicesQuestionIds.countStartId ||
      question.label === 'How many devices did you bring to class?';
    const isDevicesCountEnd =
      question.id === devicesQuestionIds.countEndId ||
      question.label === 'How many devices do you have now?';

    switch (question.type) {
      case 'long-text':
        return (
          <textarea
            id={question.id}
            value={formData[question.id] || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={4}
          />
        );
      case 'number':
        if (isSessionRatingField) {
          const value =
            typeof formData[question.id] === 'number'
              ? formData[question.id]
              : undefined;

          return (
            <div className="rating-bubbles">
              {Array.from({ length: 10 }, (_, idx) => {
                const bubbleValue = idx + 1;
                const selected = value === bubbleValue;
                return (
                  <button
                    key={bubbleValue}
                    type="button"
                    className={`rating-bubble ${selected ? 'selected' : ''}`}
                    onClick={() => handleInputChange(question.id, bubbleValue)}
                    disabled={isSubmitting || isCanceling}
                  >
                    {bubbleValue}
                  </button>
                );
              })}
            </div>
          );
        }
        if (isDevicesCountStart || isDevicesCountEnd) {
          const value = typeof formData[question.id] === 'number'
            ? formData[question.id]
            : 0;
          return (
            <div className="slider-with-value">
              <input
                id={question.id}
                type="range"
                min={0}
                max={40}
                step={1}
                value={value}
                onChange={(e) => handleInputChange(question.id, parseInt(e.target.value, 10))}
              />
              <span className="slider-value">{value}</span>
            </div>
          );
        }
        return (
          <input
            id={question.id}
            type="number"
            value={formData[question.id] || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
          />
        );
      case 'rating':
        {
          const min = question.config?.min || 1;
          const max = question.config?.max || 10;
          const value =
            typeof formData[question.id] === 'number'
              ? formData[question.id]
              : min;

          let emoji = '😐';
          if (value <= 3) {
            emoji = '☹️';
          } else if (value >= 8) {
            emoji = '🤩';
          }

        return (
          <div className="rating-input">
            <input
              id={question.id}
              type="range"
                min={min}
                max={max}
                value={value}
                onChange={(e) => handleInputChange(question.id, parseInt(e.target.value, 10))}
            />
            <div className="rating-display">
                <span className="rating-emoji">{emoji}</span>
                <span className="rating-score">
                  {value} / {max}
                </span>
            </div>
          </div>
        );
        }
      case 'text':
        if (forceLongText) {
          return (
            <textarea
              id={question.id}
              value={formData[question.id] || ''}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              rows={4}
            />
          );
        }
        return (
          <input
            id={question.id}
            type="text"
            value={formData[question.id] || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
          />
        );
      // Add more cases here for boolean-with-details, etc.
      default:
        if (forceLongText) {
          return (
            <textarea
              id={question.id}
              value={formData[question.id] || ''}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              rows={4}
            />
          );
        }
        return (
          <input
            id={question.id}
            type="text"
            value={formData[question.id] || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="feedback-modal-overlay" onClick={handleRequestClose}>
      <div className="feedback-modal" onClick={e => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2>Session Feedback</h2>
            <button className="close-modal-btn" onClick={handleRequestClose} disabled={isSubmitting || isCanceling}>&times;</button>
        </div>
        <div className="feedback-modal-content">
          {(formLoading || studentsLoading) && <p>Loading session data...</p>}
          {formError && <p className="error-message">Error: {formError.message}</p>}
          {formConfig && !studentsLoading && (
            <>
              <SessionStatsAccordion 
                squad={squad} 
                students={students} 
                achievementProgress={achievementProgress}
                teams={teams}
                entries={entries}
                onUploadWork={
                  teams && plan
                    ? () => setUploadOpen(true)
                    : undefined
                }
              />
              <form id="feedbackForm" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <div className="form-section">
                    {(orderedQuestions.length ? orderedQuestions : formConfig.questions).map(question => {
                      const lowerLabel = (question.label || '').toLowerCase();
                      const isSessionRatingField =
                        lowerLabel.includes('rate this session') ||
                        lowerLabel.includes('session out of 10');

                      let displayLabel = question.label;
                      if (lowerLabel.includes('other notable incidents')) {
                        displayLabel = 'Were there any other notable incidents? (behavioral, etc.)';
                      }

                      const control = renderQuestion(question);
                      if (control === null) return null;

                      return (
                        <React.Fragment key={question.id}>
                          <div className="form-field">
                            <label htmlFor={question.id}>{displayLabel}</label>
                            {control}
                          </div>

                          {isSessionRatingField && (
                            <>
                              <div className="form-field">
                                <label htmlFor={DEVICES_BROUGHT_KEY}>💻 Did you bring any Overture devices to class?</label>
                                <div className="yes-no-group">
                                  <label className="yes-no-option">
                                    <input
                                      type="radio"
                                      name={DEVICES_BROUGHT_KEY}
                                      value="yes"
                                      checked={formData[DEVICES_BROUGHT_KEY] === 'yes'}
                                      onChange={() => handleInputChange(DEVICES_BROUGHT_KEY, 'yes')}
                                      disabled={isSubmitting || isCanceling}
                                    />
                                    <span>Yes</span>
                                  </label>
                                  <label className="yes-no-option">
                                    <input
                                      type="radio"
                                      name={DEVICES_BROUGHT_KEY}
                                      value="no"
                                      checked={formData[DEVICES_BROUGHT_KEY] === 'no'}
                                      onChange={() => handleInputChange(DEVICES_BROUGHT_KEY, 'no')}
                                      disabled={isSubmitting || isCanceling}
                                    />
                                    <span>No</span>
                                  </label>
                                </div>
                              </div>

                              {formData[DEVICES_BROUGHT_KEY] === 'yes' && (
                                <>
                                  <div className="form-field">
                                    <label htmlFor={DEVICES_START_KEY}>How many devices did you bring to class?</label>
                                    <div className="slider-with-value">
                                      <input
                                        type="text"
                                        className="slider-number-input"
                                        inputMode="numeric"
                                        min={0}
                                        max={40}
                                        value={
                                          typeof formData[DEVICES_START_KEY] === 'number'
                                            ? formData[DEVICES_START_KEY]
                                            : 0
                                        }
                                        onChange={(e) => {
                                          const raw = Number(e.target.value);
                                          const clamped = Number.isFinite(raw)
                                            ? Math.min(40, Math.max(0, raw))
                                            : 0;
                                          handleInputChange(DEVICES_START_KEY, clamped);
                                        }}
                                      />
                                      <input
                                        id={DEVICES_START_KEY}
                                        type="range"
                                        min={0}
                                        max={40}
                                        step={1}
                                        value={
                                          typeof formData[DEVICES_START_KEY] === 'number'
                                            ? formData[DEVICES_START_KEY]
                                            : 0
                                        }
                                        onChange={(e) =>
                                          handleInputChange(DEVICES_START_KEY, parseInt(e.target.value, 10))
                                        }
                                      />
                                    </div>
                                  </div>

                                  <div className="form-field">
                                    <label htmlFor={DEVICES_END_KEY}>How many devices do you have now?</label>
                                    <div className="slider-with-value">
                                      <input
                                        type="text"
                                        className="slider-number-input"
                                        inputMode="numeric"
                                        min={0}
                                        max={40}
                                        value={
                                          typeof formData[DEVICES_END_KEY] === 'number'
                                            ? formData[DEVICES_END_KEY]
                                            : 0
                                        }
                                        onChange={(e) => {
                                          const raw = Number(e.target.value);
                                          const clamped = Number.isFinite(raw)
                                            ? Math.min(40, Math.max(0, raw))
                                            : 0;
                                          handleInputChange(DEVICES_END_KEY, clamped);
                                        }}
                                      />
                                      <input
                                        id={DEVICES_END_KEY}
                                        type="range"
                                        min={0}
                                        max={40}
                                        step={1}
                                        value={
                                          typeof formData[DEVICES_END_KEY] === 'number'
                                            ? formData[DEVICES_END_KEY]
                                            : 0
                                        }
                                        onChange={(e) =>
                                          handleInputChange(DEVICES_END_KEY, parseInt(e.target.value, 10))
                                        }
                                      />
                                    </div>
                                  </div>

                                  <div className="form-field">
                                    <label htmlFor={DEVICES_NEXT_KEY}>
                                      Who are you giving the devices to next? (say myself if you are keeping them)
                                    </label>
                                    <input
                                      type="text"
                                      id={DEVICES_NEXT_KEY}
                                      value={formData[DEVICES_NEXT_KEY] || ''}
                                      onChange={(e) => handleInputChange(DEVICES_NEXT_KEY, e.target.value)}
                                      placeholder="e.g., Myself, classroom teacher, front office..."
                                    />
                     </div>
                                </>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>
              </form>
            </>
          )}
        </div>
        <div className="feedback-modal-footer">
            <div className="draft-status" aria-live="polite">
              {restoredFromDraft && <span className="draft-restored">Draft restored</span>}
              {lastDraftSavedAt && (
                <span className="draft-saved">
                  Saved {new Date(lastDraftSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <button className="cancel-btn" onClick={handleRequestClose} disabled={isSubmitting || isCanceling}>
              ← Back
            </button>
            <button
              className="cancel-level-btn"
              onClick={handleOpenCancelLevel}
              disabled={isSubmitting || isCanceling}
            >
              Cancel Level
            </button>
          <button 
            className="submit-btn" 
            form="feedbackForm" 
            type="submit" 
              disabled={isSubmitting || isCanceling || !isFormValid}
            title={!isFormValid ? 'Please fill out all required fields' : 'Submit Feedback'}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
          <button 
            className="cancel-btn" 
            onClick={handleFinishWithoutFeedback} 
              disabled={isSubmitting || isCanceling}
          >
            Finish without feedback
          </button>
        </div>
      </div>
    </div>

      {showCancelLevelModal && (
        <div className="cancel-level-modal-overlay" onClick={() => !isCanceling && setShowCancelLevelModal(false)}>
          <div className="cancel-level-modal" onClick={e => e.stopPropagation()}>
            <h3>Cancel this level?</h3>
            <p className="cancel-level-description">
              This will mark the current session as <strong>canceled</strong> so it does not count as a taught class in reports.
            </p>
            <div className="cancel-reasons">
              <label className="cancel-reason-option">
                <input
                  type="radio"
                  name="cancel-reason"
                  value="accidental"
                  checked={cancelReason === 'accidental'}
                  onChange={() => setCancelReason('accidental')}
                  disabled={isCanceling}
                />
                <span>I accidentally started the level</span>
              </label>
              <label className="cancel-reason-option">
                <input
                  type="radio"
                  name="cancel-reason"
                  value="otherTeacher"
                  checked={cancelReason === 'otherTeacher'}
                  onChange={() => setCancelReason('otherTeacher')}
                  disabled={isCanceling}
                />
                <span>The level was started by a different teacher</span>
              </label>
              <label className="cancel-reason-option">
                <input
                  type="radio"
                  name="cancel-reason"
                  value="preview"
                  checked={cancelReason === 'preview'}
                  onChange={() => setCancelReason('preview')}
                  disabled={isCanceling}
                />
                <span>I wanted to see what the level looked like before teaching</span>
              </label>
              <label className="cancel-reason-option">
                <input
                  type="radio"
                  name="cancel-reason"
                  value="other"
                  checked={cancelReason === 'other'}
                  onChange={() => setCancelReason('other')}
                  disabled={isCanceling}
                />
                <span>Other</span>
              </label>
              {cancelReason === 'other' && (
                <textarea
                  className="cancel-reason-textarea"
                  placeholder="Type here..."
                  value={cancelReasonOther}
                  onChange={(e) => setCancelReasonOther(e.target.value)}
                  disabled={isCanceling}
                />
              )}
            </div>
            <div className="cancel-level-modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowCancelLevelModal(false)}
                disabled={isCanceling}
              >
                Back
              </button>
              <button
                className="cancel-level-btn"
                onClick={handleConfirmCancelLevel}
                disabled={isCanceling}
              >
                {isCanceling ? 'Canceling...' : 'Confirm Cancel Level'}
              </button>
            </div>
          </div>
        </div>
      )}

      {teams && plan ? (
        <UploadPhotosModal
          isOpen={uploadOpen}
          onClose={() => setUploadOpen(false)}
          squadId={squad.id}
          teams={teams}
          plan={plan}
          initialSlotIndex={pagerIndex ?? undefined}
          liveSessionId={squad.liveSession?.sessionId || null}
          classStatus={squad.classStatus}
          currentLessonId={squad.currentLessonId}
        />
      ) : null}
    </>
  );
};

export default FeedbackModal; 